// Garde anti-double-facturation de la facture mensuelle de commission proprio
// (série P). Source de vérité = table `invoices` (depuis #408), PAS la KV
// `fbz8-factlog`. Helper PUR (testable) : aucune dépendance Supabase/React.
//
// Trouve une facture commission proprio BLOQUANTE pour (logId, monthLabel).
// `rows` = lignes BRUTES de la table invoices (shape complet, PAS le shape
// aminci produit par mapInvoiceRow). Retourne la ligne bloquante ou null.
//
// Critères de blocage (tous requis) :
//   - invoice_type === 'invoice'            (exclut credit_note/avoirs)
//   - status !== 'cancelled'                (une annulée ne bloque pas)
//   - reservation_id == null                (commission mensuelle ≠ facture voyageur)
//   - prefix ∈ {'P','LEGACY'}               (série commission FBZ + legacy)
//   - meta.mois === monthLabel              (égalité STRICTE, pas de fallback date)
//   - logement_id === logId
export function findBlockingCommission(rows, { logId, monthLabel }) {
  if (!Array.isArray(rows)) return null
  return (
    rows.find(
      (r) =>
        r.invoice_type === 'invoice' &&
        r.status !== 'cancelled' &&
        r.reservation_id == null &&
        (r.prefix === 'P' || r.prefix === 'LEGACY') &&
        (r.meta && r.meta.mois) === monthLabel &&
        r.logement_id === logId,
    ) || null
  )
}

// Trouve une facture VOYAGEUR (série V, sous-loc Maison Zen) BLOQUANTE pour une
// résa donnée. Sert au gel par résa de l'édition montant (L1 V2-edit-montant) :
// une chambre sous-loc n'a PAS de facture commission P (liée au parent), mais
// elle émet des factures voyageur V par réservation. Si la facture V d'une résa
// est déjà émise, son montant est FIGÉ (le snapshot HTML a été pris).
// `rows` = lignes BRUTES de la table invoices. Retourne la ligne bloquante ou null.
//
// Critères (tous requis) :
//   - invoice_type === 'invoice'            (exclut credit_note/avoirs)
//   - status !== 'cancelled'                (une annulée ne bloque pas)
//   - prefix === 'V'                        (série voyageur Maison Zen)
//   - reservation_id === reservationId      (gel par résa, id table reservations)
export function findBlockingVoyageurInvoice(rows, { reservationId }) {
  if (!Array.isArray(rows)) return null
  if (reservationId == null) return null
  return (
    rows.find(
      (r) =>
        r.invoice_type === 'invoice' &&
        r.status !== 'cancelled' &&
        r.prefix === 'V' &&
        r.reservation_id === reservationId,
    ) || null
  )
}

// Trouve une facture VOYAGEUR (série V, sous-loc Maison Zen) BLOQUANTE pour un
// LOGEMENT/MOIS donné (Lot 4 V2-create-manual). Sert au GEL de la CRÉATION d'une
// résa manuelle : findBlockingVoyageurInvoice gèle PAR reservationId, inapplicable
// à une création (la résa n'a pas encore d'id). Ce miroir gèle PAR (logement, mois)
// — si UNE facture V est déjà émise sur ce logement/mois, on interdit d'y ajouter
// une manuelle (le snapshot du mois facturé serait incohérent).
// `rows` = lignes BRUTES de la table invoices. Retourne la ligne bloquante ou null.
//
// Critères (tous requis), SANS condition reservation_id (≠ findBlockingVoyageurInvoice) :
//   - invoice_type === 'invoice'            (exclut credit_note/avoirs)
//   - status !== 'cancelled'                (une annulée ne bloque pas)
//   - prefix === 'V'                        (série voyageur Maison Zen)
//   - logement_id === logId
//   - meta.mois === monthLabel              (égalité STRICTE, miroir commission)
export function findBlockingVoyageurInvoiceForMonth(rows, { logId, monthLabel }) {
  if (!Array.isArray(rows)) return null
  return (
    rows.find(
      (r) =>
        r.invoice_type === 'invoice' &&
        r.status !== 'cancelled' &&
        r.prefix === 'V' &&
        r.logement_id === logId &&
        (r.meta && r.meta.mois) === monthLabel,
    ) || null
  )
}

// Une facture commission proprio est-elle DÉJÀ émise pour (logId, monthLabel) ?
// Mêmes critères que findBlockingCommission (#408) → même source de vérité que
// la garde anti-double-facturation. Sert au badge « factures en attente » :
// un logement qui a déjà sa facture commission n'est plus « en attente ».
export function hasCommissionInvoice(rows, { logId, monthLabel }) {
  return findBlockingCommission(rows, { logId, monthLabel }) != null
}
